import { Alert, Card, Spin, Table, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import React, { useEffect, useMemo, useState } from 'react'
import { fetchCrowsNestFunnel } from 'src/utils/crowsNestApi'
import {
  CROWS_NEST_FUNNEL_STAGES,
  formatFunnelRate,
  formatFunnelScopeLabel,
  mapFunnelSummary,
  shouldCelebrateFunnelPaywalls,
} from 'src/utils/crowsNestFunnel'
import {
  CROWS_NEST_FUNNEL_STAGE_TIPS,
  CROWS_NEST_TOOLTIPS,
} from 'src/utils/crowsNestTooltips'
import { crowsNestErrorMessage } from 'src/utils/crowsNestErrors'
import styled from 'styled-components/macro'
import CrowsNestTipLabel from './CrowsNestTipLabel'

const FUNNEL_PLAN = 'Free'

const compareCount = (a, b, key) => (a[key] || 0) - (b[key] || 0)

const compareRate = (a, b, key) => {
  const left = a.rates?.[key]
  const right = b.rates?.[key]
  const leftVal = left == null || Number.isNaN(Number(left)) ? -1 : Number(left)
  const rightVal =
    right == null || Number.isNaN(Number(right)) ? -1 : Number(right)
  return leftVal - rightVal
}

/**
 * Simple funnel + per-feature stats — where the ladder is leaking.
 * Defaults to Free plan (free-nudge audience) for last 30 days (API default).
 */
const CrowsNestFunnelStats = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchCrowsNestFunnel({ plan: FUNNEL_PLAN })
        if (cancelled) return
        setSummary(mapFunnelSummary(response))
      } catch (err) {
        if (!cancelled) {
          setError(
            crowsNestErrorMessage(
              err,
              "Couldn't load funnel stats. Try again in a moment.",
            ),
          )
          setSummary(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  const columns = useMemo(
    () => [
      {
        title: 'Feature',
        dataIndex: 'featureName',
        key: 'feature',
        sorter: (a, b) =>
          String(a.featureName || '').localeCompare(
            String(b.featureName || ''),
          ),
        render: (name, row) => (
          <FeatureCell>
            <strong>{name}</strong>
            {row.leakHint ? (
              <Tooltip title={CROWS_NEST_TOOLTIPS.leakHint}>
                <LeakHint>{row.leakHint}</LeakHint>
              </Tooltip>
            ) : null}
          </FeatureCell>
        ),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel label='Views' tip={CROWS_NEST_TOOLTIPS.views} />
          </HeaderOneLine>
        ),
        dataIndex: 'views',
        key: 'views',
        width: 110,
        sorter: (a, b) => compareCount(a, b, 'views'),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel
              label='Clicks'
              tip={CROWS_NEST_TOOLTIPS.clicks}
            />
          </HeaderOneLine>
        ),
        dataIndex: 'clicks',
        key: 'clicks',
        width: 110,
        sorter: (a, b) => compareCount(a, b, 'clicks'),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel
              label='Opened'
              tip={CROWS_NEST_TOOLTIPS.opened}
            />
          </HeaderOneLine>
        ),
        dataIndex: 'opened',
        key: 'opened',
        width: 110,
        sorter: (a, b) => compareCount(a, b, 'opened'),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel
              label='First success'
              tip={CROWS_NEST_TOOLTIPS.firstSuccess}
            />
          </HeaderOneLine>
        ),
        dataIndex: 'firstSuccess',
        key: 'firstSuccess',
        width: 148,
        sorter: (a, b) => compareCount(a, b, 'firstSuccess'),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel
              label='Repeat use'
              tip={CROWS_NEST_TOOLTIPS.repeatUse}
            />
          </HeaderOneLine>
        ),
        dataIndex: 'repeatUse',
        key: 'repeatUse',
        width: 130,
        sorter: (a, b) => compareCount(a, b, 'repeatUse'),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel
              label='Paywalls'
              tip={CROWS_NEST_TOOLTIPS.paywalls}
            />
          </HeaderOneLine>
        ),
        dataIndex: 'paywalls',
        key: 'paywalls',
        width: 130,
        defaultSortOrder: 'descend',
        sorter: (a, b) => compareCount(a, b, 'paywalls'),
        render: (value, row) => (
          <PaywallCount
            $active={Boolean(row.paywallTrust?.trusted) && Number(value) > 0}
            data-testid={
              Boolean(row.paywallTrust?.trusted) && Number(value) > 0
                ? 'funnel-paywall-count-active'
                : undefined
            }
          >
            {value}
          </PaywallCount>
        ),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel label='CTR' tip={CROWS_NEST_TOOLTIPS.ctr} />
          </HeaderOneLine>
        ),
        key: 'ctr',
        width: 100,
        sorter: (a, b) => compareRate(a, b, 'clickThrough'),
        render: (_value, row) => formatFunnelRate(row.rates.clickThrough),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel
              label='Opened → first success'
              tip={CROWS_NEST_TOOLTIPS.openedToFirstSuccess}
            />
          </HeaderOneLine>
        ),
        key: 'openedToFirstSuccess',
        width: 196,
        sorter: (a, b) => compareRate(a, b, 'firstSuccessFromOpened'),
        render: (_value, row) =>
          formatFunnelRate(row.rates.firstSuccessFromOpened),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel
              label='First success → repeat'
              tip={CROWS_NEST_TOOLTIPS.firstSuccessToRepeat}
            />
          </HeaderOneLine>
        ),
        key: 'firstSuccessToRepeat',
        width: 196,
        sorter: (a, b) => compareRate(a, b, 'repeatFromFirstSuccess'),
        render: (_value, row) =>
          formatFunnelRate(row.rates.repeatFromFirstSuccess),
      },
      {
        title: (
          <HeaderOneLine>
            <CrowsNestTipLabel
              label='First success → paywall'
              tip={CROWS_NEST_TOOLTIPS.firstSuccessToPaywall}
            />
          </HeaderOneLine>
        ),
        key: 'firstSuccessToPaywall',
        width: 204,
        sorter: (a, b) => compareRate(a, b, 'paywallFromFirstSuccess'),
        render: (_value, row) =>
          formatFunnelRate(row.rates.paywallFromFirstSuccess),
      },
    ],
    [],
  )

  const totals = summary?.totals
  const celebratePaywalls = shouldCelebrateFunnelPaywalls(totals)
  const paywallTrustNote = totals?.paywallTrust?.copy
  const scopeLabel = formatFunnelScopeLabel(summary?.range, FUNNEL_PLAN)

  return (
    <StatsCard
      data-testid='crows-nest-funnel-stats'
      title={
        <TitleBlock>
          <TitleRow>
            <span>Funnel by feature</span>
            <Tooltip title={CROWS_NEST_TOOLTIPS.funnelSection}>
              <InfoCircleOutlined />
            </Tooltip>
          </TitleRow>
          {summary ? (
            <ScopeLabel data-testid='funnel-scope-label'>
              {scopeLabel}
            </ScopeLabel>
          ) : null}
        </TitleBlock>
      }
    >
      {error && !summary ? (
        <Alert type='error' showIcon message={error} />
      ) : null}

      {loading && !summary ? (
        <LoadingWrap>
          <Spin />
        </LoadingWrap>
      ) : null}

      {totals ? (
        <>
          <FunnelRow data-testid='funnel-totals'>
            {CROWS_NEST_FUNNEL_STAGES.map((stage, index) => (
              <React.Fragment key={stage.key}>
                {index > 0 ? <Arrow aria-hidden>→</Arrow> : null}
                <StageStat
                  $celebrate={Boolean(stage.celebrate && celebratePaywalls)}
                  data-testid={
                    stage.celebrate && celebratePaywalls
                      ? 'funnel-stage-paywalls-celebrate'
                      : undefined
                  }
                >
                  <StageValueRow>
                    <StageValue data-testid={`funnel-total-${stage.key}`}>
                      {totals[stage.key]}
                    </StageValue>
                    {stage.celebrate && celebratePaywalls ? (
                      <CelebrateMark
                        aria-label='Paywalls are what we celebrate'
                        title='What we celebrate'
                        data-testid='funnel-paywalls-celebration'
                      >
                        🎉
                      </CelebrateMark>
                    ) : null}
                  </StageValueRow>
                  <StageLabel>
                    <CrowsNestTipLabel
                      label={stage.label}
                      tip={CROWS_NEST_FUNNEL_STAGE_TIPS[stage.key]}
                    />
                  </StageLabel>
                  <StageMeta>{stage.description}</StageMeta>
                </StageStat>
              </React.Fragment>
            ))}
          </FunnelRow>

          {paywallTrustNote ? (
            <TrustNote data-testid='funnel-paywall-trust-note'>
              <Alert type='warning' showIcon message={paywallTrustNote} />
            </TrustNote>
          ) : null}

          <RatesRow>
            <Tooltip title={CROWS_NEST_TOOLTIPS.ctr}>
              <RateChip>
                CTR {formatFunnelRate(totals.rates.clickThrough)}
              </RateChip>
            </Tooltip>
            <Tooltip title={CROWS_NEST_TOOLTIPS.openedToFirstSuccess}>
              <RateChip>
                Opened → first success{' '}
                {formatFunnelRate(totals.rates.firstSuccessFromOpened)}
              </RateChip>
            </Tooltip>
            <Tooltip title={CROWS_NEST_TOOLTIPS.firstSuccessToRepeat}>
              <RateChip>
                First success → repeat{' '}
                {formatFunnelRate(totals.rates.repeatFromFirstSuccess)}
              </RateChip>
            </Tooltip>
            <Tooltip title={CROWS_NEST_TOOLTIPS.firstSuccessToPaywall}>
              <RateChip>
                First success → paywall{' '}
                {formatFunnelRate(totals.rates.paywallFromFirstSuccess)}
              </RateChip>
            </Tooltip>
          </RatesRow>

          <Table
            rowKey='key'
            columns={columns}
            dataSource={summary.byFeature}
            pagination={false}
            size='middle'
            locale={{ emptyText: 'No funnel events yet for Free venues.' }}
            scroll={{ x: true }}
          />
        </>
      ) : null}
    </StatsCard>
  )
}

const StatsCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
  }

  .ant-table-thead > tr > th {
    white-space: nowrap;
  }
`

const HeaderOneLine = styled.span`
  display: inline-flex;
  white-space: nowrap;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TitleRow = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;

  .anticon {
    color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
    font-size: 14px;
  }
`

const ScopeLabel = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 12px;
  font-weight: 500;
`

const LoadingWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 120px;
`

const FunnelRow = styled.div`
  align-items: stretch;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm || '8px'};
  margin-bottom: ${({ theme }) => theme.spacing.md || '16px'};
`

const StageStat = styled.div`
  background: ${({ $celebrate, theme }) =>
    $celebrate
      ? 'rgba(124, 179, 66, 0.12)'
      : theme.colours.bgGrey || '#f7f7f7'};
  border: 1px solid
    ${({ $celebrate, theme }) =>
      $celebrate ? theme.colours.lettuceGreen || '#7cb305' : 'transparent'};
  border-radius: 8px;
  flex: 1 1 120px;
  min-width: 110px;
  padding: 12px 14px;
`

const StageValueRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`

const StageValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
`

const CelebrateMark = styled.span`
  font-size: 22px;
  line-height: 1;
  user-select: none;
`

const StageLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
`

const StageMeta = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 11px;
  margin-top: 2px;
`

const Arrow = styled.span`
  align-self: center;
  color: ${({ theme }) => theme.colours.oliveGrey || '#999'};
  font-size: 18px;
  padding: 0 2px;
`

const TrustNote = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md || '16px'};
`

const RatesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.md || '16px'};
`

const RateChip = styled.span`
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  border: 1px solid ${({ theme }) => theme.colours.borderGrey || '#e5e5e5'};
  border-radius: 999px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#555'};
  cursor: help;
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
`

const FeatureCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const LeakHint = styled.span`
  color: ${({ theme }) => theme.colours.tomatoRed || '#b33a3a'};
  cursor: help;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
`

const PaywallCount = styled.span`
  background: ${({ $active }) =>
    $active ? 'rgba(124, 179, 66, 0.16)' : 'transparent'};
  border-radius: 6px;
  color: ${({ $active, theme }) =>
    $active ? theme.colours.seaweedGreen || '#2f6b3a' : 'inherit'};
  display: inline-block;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  min-width: 1.5em;
  padding: ${({ $active }) => ($active ? '2px 8px' : '0')};
`

export default CrowsNestFunnelStats
